/**
 * Sheet view formulas (docs/specs/database-formulas.md § Phase 2): formulas in single cells and summary rows, with
 * relative/absolute references, summary cells and references to other databases. Pure; the engine runs on the server.
 *
 * Stored references extend Phase 1's (`logic/formula`) and are read by every function here:
 *   `{p}`                       this row's cell (prop("Name"))
 *   `{r:p}` `{$r:$p}` `{$r:p}`  a cell; `$` marks an absolute row / column part (C7, $C$7, C$7)
 *   `{r:p..r:p}`                a range (each end keeps its own `$`)
 *   `{*:p}` `{*:$p}`            a whole column: relative (C:C) or absolute (typed as $C:$C or by name)
 *   `{db!…}`                    the same in another database of the workspace ('Projects'!Budget, Projects!C2)
 * Row ids may be summary rows (`Database.summaryRows`): the grid is the data rows in manual (rank) order, then the
 * summary rows; columns are the database's property order (title = A).
 */
import type { Database, Row } from "@bundle/contracts";
import { columnIndex, columnLetter, columnName, gridRowIds } from "./formula.ts";
import { formatDay, parseDate } from "./database.ts";

/** Cell formulas per database. */
export const SHEET_MAX_CELL_FORMULAS = 20_000;
/** Summary rows per database. */
export const SHEET_MAX_SUMMARY_ROWS = 200;
/** Cells one batch write (fill, paste) may change. */
export const SHEET_MAX_BATCH = 10_000;
/** Other databases one database's formulas may read. */
export const SHEET_MAX_LINKED_DATABASES = 8;

/** A database as an A1 grid: columns in property order, rows = data rows (rank order) then summary rows. */
export interface SheetGrid {
  id: string;
  title: string;
  properties: { id: string; name: string }[];
  rowIds: string[];
  /** How many of `rowIds` are data rows (the rest are summary rows). */
  dataRows: number;
}

/** The grid a formula lives in, and the other databases it may name (for typed text and display). */
export interface SheetContext {
  grid: SheetGrid;
  others?: SheetGrid[];
}

/** One end of a cell reference. */
export interface SheetCell {
  rowId: string;
  propertyId: string;
  absRow?: boolean;
  absCol?: boolean;
}

export type SheetRef =
  | { kind: "this"; propertyId: string }
  | { kind: "cell"; databaseId?: string; cell: SheetCell }
  | { kind: "range"; databaseId?: string; from: SheetCell; to: SheetCell }
  | { kind: "column"; databaseId?: string; propertyId: string; absolute?: boolean };

/** The grid of a database: data rows in rank order, then its summary rows. */
export function sheetGrid(db: Database, rows: Row[]): SheetGrid {
  const data = gridRowIds(rows);
  return {
    id: db.id,
    title: db.title,
    properties: db.properties.map((p) => ({ id: p.id, name: p.name })),
    rowIds: [...data, ...(db.summaryRows ?? []).map((s) => s.id)],
    dataRows: data.length,
  };
}

/** How a database's name is typed before `!`: as is when it reads as a name, else in single quotes ('' escapes). */
export function sheetName(title: string): string {
  const plain = /^[\p{L}_][\p{L}\p{N}_]*$/u.test(title) && !/^[A-Za-z]{1,3}[0-9]+$/.test(title) && !/^(TRUE|FALSE)$/i.test(title);
  return plain ? title : `'${title.replace(/'/g, "''")}'`;
}

// ---------- Stored references ----------

type Piece = { kind: "text" | "string" | "ref"; text: string };

/** Splits text into string literals ("…" with "" escapes), stored `{…}` references and everything else. */
function pieces(s: string): Piece[] {
  const out: Piece[] = [];
  let buf = "";
  let i = 0;
  const flush = () => {
    if (buf !== "") out.push({ kind: "text", text: buf });
    buf = "";
  };
  while (i < s.length) {
    const ch = s[i]!;
    if (ch === '"') {
      let j = i + 1;
      while (j < s.length) {
        if (s[j] === '"') {
          if (s[j + 1] === '"') j += 2;
          else break;
        } else j++;
      }
      flush();
      out.push({ kind: "string", text: s.slice(i, Math.min(j + 1, s.length)) });
      i = j + 1;
    } else if (ch === "{") {
      const end = s.indexOf("}", i);
      if (end < 0) {
        buf += s.slice(i);
        break;
      }
      flush();
      out.push({ kind: "ref", text: s.slice(i, end + 1) });
      i = end + 1;
    } else {
      buf += ch;
      i++;
    }
  }
  flush();
  return out;
}

const ID = /^[^\s:{}!$*]+$/;

function parseCell(s: string): SheetCell | undefined {
  const k = s.indexOf(":");
  if (k <= 0) return undefined;
  let row = s.slice(0, k);
  let col = s.slice(k + 1);
  const absRow = row.startsWith("$");
  const absCol = col.startsWith("$");
  if (absRow) row = row.slice(1);
  if (absCol) col = col.slice(1);
  if (!ID.test(row) || !ID.test(col) || row.includes("..") || col.includes("..")) return undefined;
  const c: SheetCell = { rowId: row, propertyId: col };
  if (absRow) c.absRow = true;
  if (absCol) c.absCol = true;
  return c;
}

/** Parses a stored reference `{…}` (braces included), Phase 1 forms included; undefined when it isn't one. */
export function parseSheetRef(text: string): SheetRef | undefined {
  if (!text.startsWith("{") || !text.endsWith("}")) return undefined;
  let inner = text.slice(1, -1);
  let databaseId: string | undefined;
  const bang = inner.indexOf("!");
  if (bang >= 0) {
    databaseId = inner.slice(0, bang);
    inner = inner.slice(bang + 1);
    if (!ID.test(databaseId)) return undefined;
  }
  const db = databaseId === undefined ? {} : { databaseId };
  if (inner === "") return undefined;
  const dots = inner.indexOf("..");
  if (dots >= 0) {
    const from = parseCell(inner.slice(0, dots));
    const to = parseCell(inner.slice(dots + 2));
    return from && to ? { kind: "range", ...db, from, to } : undefined;
  }
  if (inner.startsWith("*:")) {
    let id = inner.slice(2);
    const absolute = id.startsWith("$");
    if (absolute) id = id.slice(1);
    if (!ID.test(id)) return undefined;
    return absolute ? { kind: "column", ...db, propertyId: id, absolute: true } : { kind: "column", ...db, propertyId: id };
  }
  if (inner.includes(":")) {
    const cell = parseCell(inner);
    return cell ? { kind: "cell", ...db, cell } : undefined;
  }
  return databaseId === undefined && ID.test(inner) ? { kind: "this", propertyId: inner } : undefined;
}

function cellText(c: SheetCell): string {
  return `${c.absRow ? "$" : ""}${c.rowId}:${c.absCol ? "$" : ""}${c.propertyId}`;
}

/** The stored text of a reference (inverse of parseSheetRef). */
export function sheetRefText(r: SheetRef): string {
  if (r.kind === "this") return `{${r.propertyId}}`;
  const db = r.databaseId === undefined ? "" : `${r.databaseId}!`;
  switch (r.kind) {
    case "cell":
      return `{${db}${cellText(r.cell)}}`;
    case "range":
      return `{${db}${cellText(r.from)}..${cellText(r.to)}}`;
    case "column":
      return `{${db}*:${r.absolute ? "$" : ""}${r.propertyId}}`;
  }
}

/** The references a stored expression holds, in order (string literals skipped). */
export function sheetRefs(expression: string): SheetRef[] {
  const out: SheetRef[] = [];
  for (const p of pieces(expression)) {
    if (p.kind !== "ref") continue;
    const r = parseSheetRef(p.text);
    if (r) out.push(r);
  }
  return out;
}

/** Ids of the other databases an expression reads (first use order; `selfId` left out). */
export function referencedDatabases(expression: string, selfId: string): string[] {
  const out: string[] = [];
  for (const r of sheetRefs(expression)) {
    if (r.kind !== "this" && r.databaseId !== undefined && r.databaseId !== selfId && !out.includes(r.databaseId)) out.push(r.databaseId);
  }
  return out;
}

// ---------- Typed text → stored form ----------

const SHEET_P = String.raw`(?:'((?:[^']|'')+)'|([\p{L}_][\p{L}\p{N}_.]*))!`;
const CELL_P = String.raw`(\$?)([A-Za-z]{1,3})(\$?)([0-9]{1,7})`;
const TOKEN = new RegExp(
  String.raw`(?<![\p{L}\p{N}_.$!'])(?:${SHEET_P})?(?:` +
    String.raw`${CELL_P}:${CELL_P}(?![\p{L}\p{N}_(!])|` + // groups 3-10: range
    String.raw`${CELL_P}(?![\p{L}\p{N}_(!])|` + // 11-14: cell
    String.raw`(\$?)([A-Za-z]{1,3}):(\$?)([A-Za-z]{1,3})(?![\p{L}\p{N}_(!])|` + // 15-18: column range
    String.raw`([\p{L}_][\p{L}\p{N}_]*)(?![\p{L}\p{N}_.(!])` + // 19: identifier
    `)`,
  "gu",
);

/**
 * Typed cell-formula text → stored form, in `ctx.grid` (a leading "=" is dropped). A1 parts keep their `$`; rows past
 * the data rows are summary rows; `Name!A1`, `'Long name'!A1:B3`, `Name!Column` and `Name!C:C` name another
 * database of `ctx.others` by title (case-insensitive; the grid's own title means itself). Unknown sheets, cells
 * past the grid and unknown columns of a named sheet become `#REF!`; `prop("Name")` is this row; column names are
 * absolute whole columns; bare names that aren't columns stay as typed. Stored references and strings pass through.
 */
export function parseSheetFormula(text: string, ctx: SheetContext): string {
  const grids = [ctx.grid, ...(ctx.others ?? [])];
  const byName = new Map<string, string>();
  for (const p of ctx.grid.properties) if (!byName.has(p.name.toLowerCase())) byName.set(p.name.toLowerCase(), p.id);
  const columns = new Map<SheetGrid, Map<string, string>>();
  const columnsOf = (g: SheetGrid) => {
    let m = columns.get(g);
    if (!m) {
      m = new Map();
      for (const p of g.properties) {
        const cn = columnName(p.name).toLowerCase();
        if (cn !== "" && !m.has(cn)) m.set(cn, p.id);
      }
      columns.set(g, m);
    }
    return m;
  };
  const list = pieces(text.replace(/^\s*=/, ""));
  for (let i = 0; i + 2 < list.length; i++) {
    const before = list[i]!;
    const str = list[i + 1]!;
    const after = list[i + 2]!;
    const open = before.kind === "text" ? /(?<![\p{L}\p{N}_.])prop\s*\(\s*$/iu.exec(before.text) : null;
    const close = after.kind === "text" ? /^\s*\)/.exec(after.text) : null;
    if (!open || str.kind !== "string" || !close) continue;
    const id = byName.get(str.text.slice(1, -1).replace(/""/g, '"').toLowerCase());
    if (!id) continue;
    before.text = before.text.slice(0, open.index);
    list.splice(i + 1, 1, { kind: "ref", text: `{${id}}` });
    after.text = after.text.slice(close[0].length);
  }
  let out = "";
  for (const piece of list) {
    if (piece.kind !== "text") {
      out += piece.text;
      continue;
    }
    out += piece.text.replace(TOKEN, (m: string, ...g: (string | undefined)[]) => {
      const named = g[0] !== undefined ? g[0].replace(/''/g, "'") : g[1];
      let grid = ctx.grid;
      if (named !== undefined) {
        const found = grids.find((x) => x.title.toLowerCase() === named.toLowerCase());
        if (!found) return "#REF!";
        grid = found;
      }
      const prefix = grid.id === ctx.grid.id ? "" : `${grid.id}!`;
      const prop = (letters: string) => grid.properties[columnIndex(letters)]?.id;
      // CELL_P groups: ($ col)(letters)($ row)(digits) → stored `[$]rowId:[$]propId`.
      const cell = (dc: string | undefined, letters: string, dr: string | undefined, digits: string): string | undefined => {
        const p = prop(letters);
        const id = grid.rowIds[Number(digits) - 1];
        return p && id ? `${dr === "$" ? "$" : ""}${id}:${dc === "$" ? "$" : ""}${p}` : undefined;
      };
      if (g[3] !== undefined && g[7] !== undefined) {
        const a = cell(g[2], g[3], g[4], g[5]!);
        const b = cell(g[6], g[7], g[8], g[9]!);
        return a && b ? `{${prefix}${a}..${b}}` : "#REF!";
      }
      if (g[11] !== undefined) {
        const a = cell(g[10], g[11], g[12], g[13]!);
        return a ? `{${prefix}${a}}` : "#REF!";
      }
      if (g[15] !== undefined && g[17] !== undefined) {
        const p = prop(g[15]);
        return p && columnIndex(g[15]) === columnIndex(g[17]) ? `{${prefix}*:${g[14] === "$" ? "$" : ""}${p}}` : "#REF!";
      }
      if (g[18] !== undefined) {
        const p = columnsOf(grid).get(g[18].toLowerCase());
        if (p) return `{${prefix}*:$${p}}`;
        return named !== undefined ? "#REF!" : m;
      }
      return m;
    });
  }
  return out;
}

// ---------- Stored form → typed text ----------

/**
 * A stored cell formula as people see and edit it in `ctx.grid`: cells and ranges in A1 with their `$`, summary rows
 * numbered after the data rows, absolute columns by name (else `$C:$C`), relative columns as `C:C`, this row as
 * `prop("Name")`, other databases prefixed with their name (`Projects!C2`, `'My projects'!Budget`). References to
 * anything the context doesn't hold read `#REF!`.
 */
export function sheetFormulaDisplay(expression: string, ctx: SheetContext): string {
  const grids = new Map([...(ctx.others ?? []), ctx.grid].map((g) => [g.id, g]));
  let out = "";
  for (const piece of pieces(expression)) {
    const r = piece.kind === "ref" ? parseSheetRef(piece.text) : undefined;
    if (r === undefined) {
      out += piece.text;
      continue;
    }
    if (r.kind === "this") {
      const p = ctx.grid.properties.find((x) => x.id === r.propertyId);
      out += p ? `prop("${p.name.replace(/"/g, '""')}")` : "#REF!";
      continue;
    }
    const grid = grids.get(r.databaseId ?? ctx.grid.id);
    if (!grid) {
      out += "#REF!";
      continue;
    }
    const prefix = grid.id === ctx.grid.id ? "" : `${sheetName(grid.title)}!`;
    const col = (id: string) => {
      const i = grid.properties.findIndex((p) => p.id === id);
      return i < 0 ? undefined : columnLetter(i);
    };
    const a1 = (c: SheetCell) => {
      const l = col(c.propertyId);
      const n = grid.rowIds.indexOf(c.rowId);
      return l === undefined || n < 0 ? undefined : `${c.absCol ? "$" : ""}${l}${c.absRow ? "$" : ""}${n + 1}`;
    };
    switch (r.kind) {
      case "cell": {
        const t = a1(r.cell);
        out += t === undefined ? "#REF!" : prefix + t;
        break;
      }
      case "range": {
        const a = a1(r.from);
        const b = a1(r.to);
        out += a === undefined || b === undefined ? "#REF!" : `${prefix}${a}:${b}`;
        break;
      }
      case "column": {
        const p = grid.properties.find((x) => x.id === r.propertyId);
        const l = col(r.propertyId);
        if (!p || l === undefined) {
          out += "#REF!";
          break;
        }
        if (!r.absolute) {
          out += `${prefix}${l}:${l}`;
          break;
        }
        const cn = columnName(p.name);
        const clash = cn !== "" && grid.properties.some((x) => x.id !== p.id && columnName(x.name).toLowerCase() === cn.toLowerCase());
        out += prefix + (cn !== "" && !clash ? cn : `$${l}:$${l}`);
        break;
      }
    }
  }
  return out;
}

// ---------- Fill, copy and paste ----------

/**
 * The formula copied `dRows` rows down and `dCols` columns right (negative: up/left), as Sheets fills: relative row
 * and column parts move through the grid (rank order including summary rows; property order), absolute parts stay,
 * and ids are stored again. A part that leaves the grid makes the reference `#REF!`. `prop("…")` doesn't move.
 * References to other databases move through their grid in `ctx.others` (unchanged when it isn't there).
 */
export function shiftFormula(expression: string, dRows: number, dCols: number, ctx: SheetContext): string {
  if (dRows === 0 && dCols === 0) return expression;
  const grids = new Map([...(ctx.others ?? []), ctx.grid].map((g) => [g.id, g]));
  const move = (grid: SheetGrid, c: SheetCell): SheetCell | null | undefined => {
    const ri = grid.rowIds.indexOf(c.rowId);
    const ci = grid.properties.findIndex((p) => p.id === c.propertyId);
    if (ri < 0 || ci < 0) return undefined; // already broken: leave it
    const r = c.absRow ? ri : ri + dRows;
    const k = c.absCol ? ci : ci + dCols;
    if (r < 0 || r >= grid.rowIds.length || k < 0 || k >= grid.properties.length) return null;
    return { ...c, rowId: grid.rowIds[r]!, propertyId: grid.properties[k]!.id };
  };
  let out = "";
  for (const piece of pieces(expression)) {
    const r = piece.kind === "ref" ? parseSheetRef(piece.text) : undefined;
    const grid = r && r.kind !== "this" ? grids.get(r.databaseId ?? ctx.grid.id) : undefined;
    if (r === undefined || r.kind === "this" || grid === undefined) {
      out += piece.text;
      continue;
    }
    switch (r.kind) {
      case "cell": {
        const c = move(grid, r.cell);
        out += c === null ? "#REF!" : c === undefined ? piece.text : sheetRefText({ ...r, cell: c });
        break;
      }
      case "range": {
        const a = move(grid, r.from);
        const b = move(grid, r.to);
        out += a === null || b === null ? "#REF!" : a === undefined || b === undefined ? piece.text : sheetRefText({ ...r, from: a, to: b });
        break;
      }
      case "column": {
        const ci = grid.properties.findIndex((p) => p.id === r.propertyId);
        if (r.absolute || ci < 0) {
          out += piece.text;
          break;
        }
        const k = ci + dCols;
        out += k < 0 || k >= grid.properties.length ? "#REF!" : sheetRefText({ ...r, propertyId: grid.properties[k]!.id });
        break;
      }
    }
  }
  return out;
}

const NUMBER = /^[-+]?(\d+\.?\d*|\.\d+)$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const NUMBERED = /^(.*?)(\d+)$/s;

const tidy = (n: number) => String(Number(n.toFixed(10)));

function addMonths(day: number, months: number): number {
  const s = formatDay(day);
  const m = Number(s.slice(5, 7)) - 1 + months;
  const year = Number(s.slice(0, 4)) + Math.floor(m / 12);
  const month = ((m % 12) + 12) % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return Math.round(Date.UTC(year, month, Math.min(Number(s.slice(8, 10)), last)) / 86_400_000);
}

function monthsBetween(a: number, b: number): number | undefined {
  const sa = formatDay(a);
  const sb = formatDay(b);
  if (sa.slice(8) !== sb.slice(8)) return undefined;
  return (Number(sb.slice(0, 4)) - Number(sa.slice(0, 4))) * 12 + Number(sb.slice(5, 7)) - Number(sa.slice(5, 7));
}

/** The constant step between consecutive values, or undefined when they don't step evenly. */
function evenStep(xs: number[]): number | undefined {
  const step = xs[1]! - xs[0]!;
  for (let i = 2; i < xs.length; i++) if (Math.abs(xs[i]! - xs[i - 1]! - step) > 1e-9) return undefined;
  return step;
}

/**
 * The next `count` values when a selection of `values` (cell text, in fill order) is dragged by the fill handle, as
 * Sheets does: one number copies; several numbers continue their linear trend (1,2 → 3,4; 1,2,4 → least squares);
 * dates (YYYY-MM-DD) step by a day when alone, else by the selection's even step in months (same day of month, end
 * of month clamped) or days; text ending in a number steps that number ("Item 1" → "Item 2", zero padding kept);
 * anything else repeats the selection. To fill up or left, pass the selection reversed.
 */
export function fillSeries(values: string[], count: number): string[] {
  if (values.length === 0 || count <= 0) return [];
  const n = values.length;
  const cycle = (i: number) => values[i % n]!;
  const make = (f: (i: number) => string) => Array.from({ length: count }, (_, i) => f(i));
  if (values.every((v) => NUMBER.test(v.trim()))) {
    if (n === 1) return make(cycle);
    const ys = values.map((v) => Number(v.trim()));
    const mx = (n - 1) / 2;
    const my = ys.reduce((a, b) => a + b, 0) / n;
    let num = 0;
    let den = 0;
    ys.forEach((y, x) => {
      num += (x - mx) * (y - my);
      den += (x - mx) * (x - mx);
    });
    const slope = num / den;
    return make((i) => tidy(my + slope * (n + i - mx)));
  }
  if (values.every((v) => DAY.test(v.trim()) && parseDate(v.trim(), 0) !== undefined)) {
    const days = values.map((v) => parseDate(v.trim(), 0)!.day);
    if (n === 1) return make((i) => formatDay(days[0]! + i + 1));
    const months = days.slice(1).map((d, i) => monthsBetween(days[i]!, d));
    const m = months[0];
    if (m !== undefined && m !== 0 && months.every((x) => x === m)) return make((i) => formatDay(addMonths(days[0]!, m * (n + i))));
    const step = evenStep(days);
    if (step !== undefined) return make((i) => formatDay(days[n - 1]! + step * (i + 1)));
    return make(cycle);
  }
  const numbered = values.map((v) => NUMBERED.exec(v));
  if (numbered.every((x) => x !== null && x[1] === numbered[0]![1])) {
    const prefix = numbered[0]![1]!;
    const digits = numbered[0]![2]!;
    const width = digits.startsWith("0") ? digits.length : 0;
    const nums = numbered.map((x) => Number(x![2]));
    const step = n === 1 ? 1 : evenStep(nums);
    if (step !== undefined) {
      return make((i) => {
        const v = nums[n - 1]! + step * (i + 1);
        return v < 0 ? cycle(i) : prefix + String(v).padStart(width, "0");
      });
    }
  }
  return make(cycle);
}

// ---------- Stored form → engine A1 ----------

/** Where a database sits in the engine model: its sheet prefix ("" or `'S2'!`) and engine rows/columns (1-based rows). */
export interface EngineSheet {
  prefix: string;
  columnOf: Record<string, number>;
  rowOf: Record<string, number>;
  /** Data rows are engine rows 1…dataRows; whole columns cover only them (never summary rows). */
  dataRows: number;
}

/** An always-blank engine row, for whole columns of a database with no data rows. */
const BLANK_ROW = 1_048_576;

/**
 * The A1 formula (no "=") the engine runs for a formula in engine row `row` of database `self` (`inData`: a data row,
 * not a summary row). Every part is absolute (the engine never moves formulas). A whole column is the column's data
 * rows as a function argument; elsewhere it's this row's cell in a data row of the same database (implicit
 * intersection), else `#VALUE!`. `{p}` outside a data row and unknown references are `#REF!`.
 */
export function sheetEngineFormula(expression: string, row: number, inData: boolean, self: string, sheets: Record<string, EngineSheet>): string {
  const list = pieces(expression);
  let out = "";
  list.forEach((piece, i) => {
    const r = piece.kind === "ref" ? parseSheetRef(piece.text) : undefined;
    if (r === undefined) {
      out += piece.text;
      return;
    }
    if (r.kind === "this") {
      const c = sheets[self]?.columnOf[r.propertyId];
      out += c === undefined || !inData ? "#REF!" : `$${columnLetter(c)}$${row}`;
      return;
    }
    const dbId = r.databaseId ?? self;
    const s = sheets[dbId];
    if (s === undefined) {
      out += "#REF!";
      return;
    }
    switch (r.kind) {
      case "cell": {
        const c = s.columnOf[r.cell.propertyId];
        const n = s.rowOf[r.cell.rowId];
        out += c === undefined || n === undefined ? "#REF!" : `${s.prefix}$${columnLetter(c)}$${n}`;
        break;
      }
      case "range": {
        const ca = s.columnOf[r.from.propertyId];
        const cb = s.columnOf[r.to.propertyId];
        const ra = s.rowOf[r.from.rowId];
        const rb = s.rowOf[r.to.rowId];
        if (ca === undefined || cb === undefined || ra === undefined || rb === undefined) {
          out += "#REF!";
          break;
        }
        out += `${s.prefix}$${columnLetter(Math.min(ca, cb))}$${Math.min(ra, rb)}:$${columnLetter(Math.max(ca, cb))}$${Math.max(ra, rb)}`;
        break;
      }
      case "column": {
        const c = s.columnOf[r.propertyId];
        if (c === undefined) {
          out += "#REF!";
          break;
        }
        const l = columnLetter(c);
        const before = list[i - 1]?.kind === "text" ? list[i - 1]!.text.trimEnd().at(-1) : undefined;
        const after = list[i + 1]?.kind === "text" ? list[i + 1]!.text.trimStart()[0] : undefined;
        const argument = (before === "(" || before === ",") && (after === ")" || after === ",");
        if (argument) {
          const first = s.dataRows > 0 ? 1 : BLANK_ROW;
          const last = s.dataRows > 0 ? s.dataRows : BLANK_ROW;
          out += `${s.prefix}$${l}$${first}:$${l}$${last}`;
        } else out += dbId === self && inData ? `$${l}$${row}` : "#VALUE!";
        break;
      }
    }
  });
  return out;
}

/** Cells one evaluation reads (this row and cells 1, ranges their area, whole columns their data rows). */
export function sheetCellsRead(expression: string, self: string, sheets: Record<string, EngineSheet>): number {
  let n = 0;
  for (const r of sheetRefs(expression)) {
    if (r.kind === "this" || r.kind === "cell") {
      n += 1;
      continue;
    }
    const s = sheets[r.databaseId ?? self];
    if (s === undefined) continue;
    if (r.kind === "column") n += s.dataRows;
    else {
      const ca = s.columnOf[r.from.propertyId];
      const cb = s.columnOf[r.to.propertyId];
      const ra = s.rowOf[r.from.rowId];
      const rb = s.rowOf[r.to.rowId];
      if (ca !== undefined && cb !== undefined && ra !== undefined && rb !== undefined) n += (Math.abs(ca - cb) + 1) * (Math.abs(ra - rb) + 1);
    }
  }
  return n;
}
